import React, { useMemo, useState } from 'react';
import { CListGroup, CPagination } from '@coreui/react';
import { useSelector } from 'react-redux';
import { maxBy, chain } from 'lodash';
import { selectChats } from '../../selectors/chat.selector';
import Contact from './Contact';
import { CONTACT_SEARCH_FIELDS } from '../../../shared/constants/chat.constants';
import ContactSearch from './ContactSearch';

interface ChatListProps {
  activeChat: string;
  onSelectChat: (chat: string) => void;
}

const ContactList = (props: ChatListProps) => {
  const {
    activeChat,
    onSelectChat
  } = props;
  const chats = useSelector(selectChats);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<any>({});
  const [currentPage, setActivePage] = useState(1);
  const [recordsPerPage] = useState(10);

  const handleQueryChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const makeSubstringBold = (item, query) => {
    if (!query) return item;
    const regex = new RegExp(query, 'gi');
    return item.replace(regex, (match) => `<span class="font-weight-bolder">${match}</span>`);
  };

  const highlightSearchQueryInChatInfo = (item, query) => {
    let result = {...item};
    let isFound = false;
    CONTACT_SEARCH_FIELDS.forEach((field) => {
      const value = item?.[field]?.toString();
      if (value?.toLowerCase?.()?.indexOf?.(query?.toLowerCase?.()) > -1) {
        result = {
          ...result,
          [field]: makeSubstringBold(value, query)
        };
        isFound = true;
      }
    });
    return isFound ? result : null;
  };

  const filteredChats = useMemo(() => {
    if (chats) {
      let chatResults = chain(chats).map(chat => chat);
      if (searchQuery) {
        chatResults = chatResults.reduce((result, item) => {
          const chatInfo = highlightSearchQueryInChatInfo(item?.chatInfo, searchQuery);
          if (chatInfo) {
            result.push({
              ...item,
              chatInfo
            });
          }
          return result;
        }, []);
      }

      return chatResults
        .orderBy((a) => {
          return maxBy(chats?.[a?.chat_id]?.messages, 'created_time')?.created_time ?? 0;
        }, 'desc')
        .filter((item) => {
          const isAgentSelected = filters?.agents?.length === 0 || filters?.agents?.indexOf(item?.chatInfo?.agent_id) > -1;
          const isOrgSelected = filters?.orgs?.length === 0 || filters?.orgs?.indexOf(item?.chatInfo?.org_id) > -1;

          return isAgentSelected && isOrgSelected;
        })
        .value();
    }
    return [];
  }, [chats, searchQuery, filters]);

  const perPageItems = useMemo(() => {
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;

    return filteredChats.slice(indexOfFirstRecord, indexOfLastRecord);
  }, [currentPage, filteredChats]);

  return (
    <div className={`chat-list ${activeChat ? 'hide' : ''}`}>
      <ContactSearch
        onFilterChange={setFilters}
        searchQuery={searchQuery}
        onQueryChange={handleQueryChange}
      />
      <div className={`chat-list-container ${activeChat ? 'hide' : ''}`}>
        {
          perPageItems?.length > 0 ? (
            <CListGroup>
              {
                perPageItems?.map((chat) => {
                  return  <Contact key={chat?.chat_id} chat={chat} activeChatId={activeChat} onSelectChat={onSelectChat}/>
                })
              }
            </CListGroup>
          ) : (
            <div className="d-flex justify-content-center align-items-stretch flex-grow-1 font-weight-bold mt-3">
              No Result Found
            </div>
          )
        }
      </div>
      {
        filteredChats?.length ? (
          <CPagination
            className="d-flex justify-content-center mt-2"
            activePage={currentPage}
            pages={Math.ceil(filteredChats.length / recordsPerPage)}
            onActivePageChange={(i) => {
              setActivePage(i)
            }}
          />
        ) : null
      }
    </div>
  );
};

export default ContactList;
